import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FileText, ChevronRight, Loader2, Download, Calendar, User, Building,
  Sparkles, Pencil, Save, Tag, Lightbulb, MapPin, X, Eye, Users as UsersIcon
} from 'lucide-react';
import { useKB } from './KBContext';
import { CONFERENCE_OPTIONS } from './constants';
import MemberSheet, { MemberAvatar } from './MemberSheet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentDetail = () => {
  const {
    selectedDoc, setSelectedDoc, password,
    editMode, setEditMode, editData, setEditData,
    saving, rescanning,
    showSavePrompt, setShowSavePrompt, editorName, setEditorName,
    startEdit, cancelEdit, triggerSave, saveEdit,
    handleAiRescan, handleDownload, handleView,
    filterOptions, formatFileSize, memberInfo
  } = useKB();

  const [matchedMembers, setMatchedMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);

  // Fetch matched members for the current document (Phase 1.7 cross-link)
  // Only surface AUTHOR matches in the UI — same-org matches are too noisy
  // (a 50-author paper from "Johnson Matthey" would otherwise list every IPMI
  // member ever employed by JM). Backend still returns both for future use cases.
  useEffect(() => {
    if (!selectedDoc?.doc_id) { setMatchedMembers([]); return; }
    let cancelled = false;
    fetch(`${API_URL}/api/kb/documents/${selectedDoc.doc_id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d) return;
        const authorsOnly = (d.matched_members || []).filter(m => m.match_type === 'author');
        setMatchedMembers(authorsOnly);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedDoc?.doc_id, password]);

  if (!selectedDoc) return null;

  return (
    <motion.div className="kb-doc-detail" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <button className="kb-back-btn" onClick={() => { setSelectedDoc(null); setEditMode(false); }} data-testid="kb-doc-back">
        <ChevronRight size={16} style={{ transform: 'rotate(180deg)' }} /> All Documents
      </button>

      {/* Manual Edit badge */}
      {selectedDoc.manually_edited && (
        <div className="kb-edited-badge" data-testid="kb-edited-badge">
          <Pencil size={12} /> Manually Edited
          {selectedDoc.last_edited_at && <span> — {new Date(selectedDoc.last_edited_at).toLocaleDateString()}</span>}
        </div>
      )}

      <div className="kb-doc-detail-header">
        <FileText size={32} />
        <div>
          {editMode ? (
            <input className="kb-edit-input kb-edit-title" value={editData.title}
              onChange={e => setEditData(p => ({ ...p, title: e.target.value }))}
              data-testid="kb-edit-title" />
          ) : (
            <h2>{selectedDoc.title}</h2>
          )}
          <div className="kb-doc-meta-row">
            <span className="kb-doc-meta-badge">{selectedDoc.file_type?.toUpperCase()}</span>
            {editMode ? (
              <select className="kb-edit-select" value={editData.category}
                onChange={e => setEditData(p => ({ ...p, category: e.target.value }))}
                data-testid="kb-edit-category">
                {filterOptions.categories?.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="Other">Other</option>
              </select>
            ) : (
              selectedDoc.category && <span className="kb-doc-meta-badge cat">{selectedDoc.category}</span>
            )}
            {editMode ? (
              <input className="kb-edit-input kb-edit-year" type="number" value={editData.year}
                onChange={e => setEditData(p => ({ ...p, year: e.target.value }))}
                placeholder="Year" data-testid="kb-edit-year" />
            ) : (
              selectedDoc.year && <span className="kb-doc-meta-badge year"><Calendar size={11} /> {selectedDoc.year}</span>
            )}
            <span className="kb-doc-meta-text">{selectedDoc.chunk_count} chunks</span>
            <span className="kb-doc-meta-text">{formatFileSize(selectedDoc.file_size)}</span>
          </div>
          {/* Conference/Event field */}
          {editMode ? (
            <div className="kb-edit-conference-row">
              <MapPin size={13} />
              <select className="kb-edit-select" value={editData.conference}
                onChange={e => setEditData(p => ({ ...p, conference: e.target.value }))}
                data-testid="kb-edit-conference">
                <option value="">— Select Conference/Event —</option>
                {CONFERENCE_OPTIONS.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          ) : (
            selectedDoc.conference && (
              <div className="kb-doc-conference-badge">
                <MapPin size={12} /> {selectedDoc.conference}
              </div>
            )
          )}
        </div>
        <div className="kb-doc-actions-row">
          {/* Editing is an admin/staff action — hidden from members (read-only). */}
          {!memberInfo && (!editMode ? (
            <button className="kb-edit-btn" onClick={startEdit} data-testid="kb-edit-start">
              <Pencil size={14} /> Edit
            </button>
          ) : (
            <>
              <button className="kb-save-btn" onClick={triggerSave} disabled={saving} data-testid="kb-edit-save">
                {saving ? <Loader2 size={14} className="kb-spin" /> : <Save size={14} />} Save
              </button>
              <button className="kb-rescan-btn" onClick={handleAiRescan} disabled={rescanning} data-testid="kb-edit-rescan">
                {rescanning ? <><Loader2 size={14} className="kb-spin" /> Rescanning...</> : <><Sparkles size={14} /> Regenerate with AI</>}
              </button>
              <button className="kb-cancel-btn" onClick={cancelEdit} data-testid="kb-edit-cancel">Cancel</button>
            </>
          ))}
          <button className="kb-download-btn" onClick={() => handleView(selectedDoc.r2_key)}
            data-testid="kb-doc-view">
            <Eye size={16} /> View
          </button>
          <button className="kb-download-btn" onClick={() => handleDownload(selectedDoc.r2_key)}
            data-testid="kb-doc-download">
            <Download size={16} /> Download
          </button>
        </div>
        {/* Save prompt for editor name */}
        {showSavePrompt && (
          <div className="kb-save-prompt" data-testid="kb-save-prompt">
            <input type="text" placeholder="Your name (optional)" value={editorName}
              onChange={(e) => setEditorName(e.target.value)} className="kb-edit-input"
              data-testid="kb-editor-name" autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveEdit()} />
            <div className="kb-save-prompt-actions">
              <button className="kb-save-btn" onClick={saveEdit} disabled={saving} data-testid="kb-save-confirm">
                {saving ? <Loader2 size={14} className="kb-spin" /> : <Save size={14} />} Confirm Save
              </button>
              <button className="kb-cancel-btn" onClick={() => setShowSavePrompt(false)}>Back</button>
            </div>
          </div>
        )}
      </div>

      <div className="kb-doc-summary-box">
        <h3>AI-Generated Summary</h3>
        {editMode ? (
          <textarea className="kb-edit-textarea" value={editData.summary}
            onChange={e => setEditData(p => ({ ...p, summary: e.target.value }))}
            rows={4} data-testid="kb-edit-summary" />
        ) : (
          <p>{selectedDoc.summary}</p>
        )}
      </div>

      {editMode ? (
        <div className="kb-edit-fields">
          <div className="kb-edit-field">
            <label><User size={13} /> Authors <span className="kb-edit-hint">(comma-separated)</span></label>
            <input className="kb-edit-input" value={editData.authors}
              onChange={e => setEditData(p => ({ ...p, authors: e.target.value }))}
              placeholder="Author 1, Author 2" data-testid="kb-edit-authors" />
          </div>
          <div className="kb-edit-field">
            <label><Building size={13} /> Organizations <span className="kb-edit-hint">(comma-separated)</span></label>
            <input className="kb-edit-input" value={editData.organizations}
              onChange={e => setEditData(p => ({ ...p, organizations: e.target.value }))}
              placeholder="Org 1, Org 2" data-testid="kb-edit-orgs" />
          </div>
          <div className="kb-edit-field">
            <label><Lightbulb size={13} /> Key Topics <span className="kb-edit-hint">(comma-separated)</span></label>
            <input className="kb-edit-input" value={editData.key_topics}
              onChange={e => setEditData(p => ({ ...p, key_topics: e.target.value }))}
              placeholder="Topic 1, Topic 2" data-testid="kb-edit-topics" />
          </div>
          <div className="kb-edit-field">
            <label><Tag size={13} /> Tags <span className="kb-edit-hint">(comma-separated)</span></label>
            <input className="kb-edit-input" value={editData.tags}
              onChange={e => setEditData(p => ({ ...p, tags: e.target.value }))}
              placeholder="tag1, tag2" data-testid="kb-edit-tags" />
          </div>
        </div>
      ) : (
        <>
          {(selectedDoc.authors?.length > 0 || selectedDoc.organizations?.length > 0) && (
            <div className="kb-doc-people-section">
              {selectedDoc.authors?.length > 0 && (
                <div className="kb-doc-people-group">
                  <h4><User size={14} /> Authors</h4>
                  <div className="kb-doc-people-list">
                    {selectedDoc.authors.map((a, i) => <span key={i} className="kb-person-chip">{a}</span>)}
                  </div>
                </div>
              )}
              {selectedDoc.organizations?.length > 0 && (
                <div className="kb-doc-people-group">
                  <h4><Building size={14} /> Organizations</h4>
                  <div className="kb-doc-people-list">
                    {selectedDoc.organizations.map((o, i) => <span key={i} className="kb-person-chip org">{o}</span>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {selectedDoc.key_topics?.length > 0 && (
            <div className="kb-doc-topics">
              <h4>Key Topics</h4>
              <div className="kb-doc-tags">
                {selectedDoc.key_topics.map((t, i) => <span key={i} className="kb-tag topic">{t}</span>)}
              </div>
            </div>
          )}

          {selectedDoc.tags?.length > 0 && (
            <div className="kb-doc-topics">
              <h4>Tags</h4>
              <div className="kb-doc-tags">
                {selectedDoc.tags.map((tag, i) => <span key={i} className="kb-tag"><Tag size={12} /> {tag}</span>)}
              </div>
            </div>
          )}

          {matchedMembers.length > 0 && (
            <div className="kb-doc-related-members" data-testid="kb-doc-related-members">
              <h4>
                <UsersIcon size={14} /> IPMI Members Authoring This Document
                <span className="kb-doc-related-count">{matchedMembers.length}</span>
              </h4>
              <p className="kb-doc-related-hint">
                Click to view contact details.
              </p>
              <div className="kb-doc-related-grid">
                {matchedMembers.map((m, i) => (
                  <button
                    key={`${m.member_id}-${i}`}
                    className="kb-doc-related-chip"
                    onClick={() => setSelectedMember(m)}
                    data-testid={`kb-doc-related-chip-${m.member_id}`}
                  >
                    <MemberAvatar member={m} size={32} />
                    <div className="kb-doc-related-chip-text">
                      <span className="kb-doc-related-chip-name">{m.full_name}</span>
                      <span className="kb-doc-related-chip-meta">{m.company_name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {selectedMember && (
            <MemberSheet member={selectedMember} onClose={() => setSelectedMember(null)} />
          )}
        </>
      )}
    </motion.div>
  );
};

export default DocumentDetail;
